import React, {
  useEffect,
  useMemo
} from "react";

import {
  Button,
  Card,
  CardBody,
  Content,
  Grid,
  GridItem,
  PageSection,
  Skeleton,
  Stack,
  StackItem
} from "@patternfly/react-core";

import {
  ArrowLeftIcon
} from "@patternfly/react-icons";

import {
  useNavigate,
  useParams
} from "react-router-dom";


import {
  normalizeWordPressHtml
} from "shared/utils/wordpressUrl";
import {
  useNewsPost
} from "../../features/news/hooks/useNewsPost";

import NewsImageGallery
  from "../../features/news/components/NewsImageGallery";

// const extractGalleryFromContent = (html) => {
//   if (!html) {
//     return {
//       content: "",
//       images: []
//     };
//   }

//   const parser = new DOMParser();
//   const documentContent = parser.parseFromString(
//     html,
//     "text/html"
//   );

//   const galleryElements = Array.from(
//     documentContent.querySelectorAll(
//       ".wp-block-gallery, .gallery"
//     )
//   );

//   const images = [];
//   const imageSources = new Set();

//   galleryElements.forEach((gallery) => {
//     gallery
//       .querySelectorAll("img")
//       .forEach((image, index) => {
//         const src =
//           image.getAttribute("src") ||
//           image.getAttribute("data-src");

//         if (!src || imageSources.has(src)) {
//           return;
//         }

//         imageSources.add(src);

//         const figure =
//           image.closest("figure");

//         const caption =
//           figure
//             ?.querySelector("figcaption")
//             ?.textContent
//             ?.trim() ?? "";

//         images.push({
//           src,
//           alt:
//             image.getAttribute("alt") ||
//             `Zdjęcie ${index + 1}`,
//           caption
//         });
//       });
//   });

//   /*
//    * Usuwamy oryginalne galerie z treści,
//    * ponieważ zostaną wyrenderowane jako mozaika.
//    */
//   galleryElements.forEach((gallery) => {
//     gallery.remove();
//   });

//   return {
//     content: documentContent.body.innerHTML,
//     images
//   };
// };
const isImageUrl = (url) => {
  if (!url) {
    return false;
  }

  const cleanUrl = url
    .split("?")[0]
    .split("#")[0]
    .toLowerCase();

  return [
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".gif",
    ".avif"
  ].some((extension) => {
    return cleanUrl.endsWith(extension);
  });
};
const extractGalleryFromContent = (html) => {
  if (!html) {
    return {
      content: "",
      images: []
    };
  }

  const parser = new DOMParser();

  const documentContent = parser.parseFromString(
    html,
    "text/html"
  );

  /*
   * Współczesne galerie WordPressa.
   */
  const modernGalleryElements = Array.from(
    documentContent.querySelectorAll(
      ".wp-block-gallery, .gallery"
    )
  );

  /*
   * Starsze galerie zapisane jako:
   *
   * <ul>
   *   <li><a><img /></a></li>
   * </ul>
   */
  const legacyGalleryElements = Array.from(
    documentContent.querySelectorAll("ul")
  ).filter((list) => {
    const listItems = Array.from(
      list.children
    ).filter((element) => {
      return element.tagName === "LI";
    });

    if (!listItems.length) {
      return false;
    }

    /*
     * Lista zostaje uznana za galerię, jeżeli
     * przynajmniej jeden jej element zawiera obraz.
     */
    return listItems.some((listItem) => {
      return Boolean(
        listItem.querySelector("img")
      );
    });
  });

  const candidates = Array.from(
    new Set([
      ...modernGalleryElements,
      ...legacyGalleryElements
    ])
  );

  /*
   * Jeżeli np. ul.wp-block-gallery znajduje się
   * w innym kontenerze galerii, zachowujemy tylko
   * najbardziej zewnętrzny element.
   */
  const galleryElements = candidates.filter(
    (candidate) => {
      return !candidates.some((otherCandidate) => {
        return (
          otherCandidate !== candidate &&
          otherCandidate.contains(candidate)
        );
      });
    }
  );

  const images = [];
  const imageSources = new Set();

  galleryElements.forEach((gallery) => {
    const galleryImages = Array.from(
      gallery.querySelectorAll("img")
    );

    galleryImages.forEach((image) => {
      const link = image.closest("a");

      /*
       * Starsze galerie często umieszczają miniaturę
       * w img, a pełne zdjęcie w href elementu a.
       */
      const linkSource =
        link?.getAttribute("href");

      const imageSource =
        image.getAttribute("data-orig-file") ||
        image.getAttribute("data-large-file") ||
        image.getAttribute("data-src") ||
        image.getAttribute("src");

      const src =
        isImageUrl(linkSource)
          ? linkSource
          : imageSource;

      if (!src || imageSources.has(src)) {
        return;
      }

      imageSources.add(src);

      const figure = image.closest("figure");

      const caption =
        figure
          ?.querySelector("figcaption")
          ?.textContent
          ?.trim() ||
        image
          .closest("li")
          ?.querySelector(".wp-caption-text")
          ?.textContent
          ?.trim() ||
        "";

      images.push({
        src,
        alt:
          image.getAttribute("alt")?.trim() ||
          `Zdjęcie ${images.length + 1}`,
        caption
      });
    });
  });

  galleryElements.forEach((gallery) => {
    gallery.remove();
  });

  return {
    content: documentContent.body.innerHTML,
    images
  };
};

const NewsDetailsPage = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto"
    });
  }, [postId]);

  const {
      post,
      loading,
      error
  } = useNewsPost(postId);

  const preparedContent = useMemo(() => {
    const normalizedContent =
      normalizeWordPressHtml(
        post?.content?.rendered || ""
      );

    return extractGalleryFromContent(
      normalizedContent
    );
  }, [
    post?.content?.rendered
  ]);

  if (loading) {
    return (
      <PageSection isWidthLimited>
        <Grid>
          <GridItem
            span={12}
            md={10}
            mdOffset={1}
            lg={8}
            lgOffset={2}
          >
            <Stack hasGutter>
              <Skeleton width="60%" />
              <Skeleton width="30%" />
              <Skeleton height="300px" />
              <Skeleton />
              <Skeleton />
              <Skeleton width="80%" />
            </Stack>
          </GridItem>
        </Grid>
      </PageSection>
    );
  }

  if (error || !post) {
    return (
      <PageSection isWidthLimited>
        <Content style={{ textAlign: "center" }}>
          <h1>Nie udało się pobrać aktualności</h1>

          <p>
            Wybrana aktualność jest niedostępna.
          </p>
        </Content>
      </PageSection>
    );
  }

  const publishDate = new Intl.DateTimeFormat(
    "pl-PL",
    {
      day: "numeric",
      month: "long",
      year: "numeric"
    }
  ).format(new Date(post.date));

  return (
    <PageSection isWidthLimited>
      <Stack hasGutter>
        <StackItem>
          <Grid>
            <GridItem
              span={12}
              md={10}
              mdOffset={1}
              lg={8}
              lgOffset={2}
            >
              <Button
                  variant="link"
                  isInline
                  icon={<ArrowLeftIcon />}
                  onClick={() => navigate(-1)}
              >
                  Wróć do aktualności
              </Button>
            </GridItem>
          </Grid>
        </StackItem>


        <StackItem>
          <Grid>
            <GridItem
              span={12}
              md={10}
              mdOffset={1}
              lg={8}
              lgOffset={2}
            >
              <Content style={{ textAlign: "center" }}>
                <h1
                  dangerouslySetInnerHTML={{
                    __html: post.title?.rendered || ""
                  }}
                />

                <p>{publishDate}</p>
              </Content>
            </GridItem>
          </Grid>
        </StackItem>


        <StackItem>
          <Grid>
            <GridItem
              span={12}
              md={10}
              mdOffset={1}
              lg={8}
              lgOffset={2}
            >
              <Card>
                <CardBody>
                  <Stack hasGutter>
                    {preparedContent.content && (
                      <StackItem>
                        <Content
                          dangerouslySetInnerHTML={{
                            __html: preparedContent.content
                          }}
                        />
                      </StackItem>
                    )}

                    {preparedContent.images.length > 0 && (
                      <StackItem>
                        <NewsImageGallery
                          images={preparedContent.images}
                        />
                      </StackItem>
                    )}
                  </Stack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
        </StackItem>
      </Stack>
    </PageSection>
  );
};


export default NewsDetailsPage;